/**
 * Section 20b — Privacy Policy (PRE_LAUNCH CL2 / F4).
 *
 * Written from general knowledge of the Digital Personal Data Protection
 * Act, 2023 (DPDP) and the IT (Reasonable Security Practices) Rules, 2011.
 * NOT a substitute for counsel review. The operator commits to a finalised
 * counsel-reviewed version before unrestricted public launch.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import GuideCallout from '../GuideCallout';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 3.5, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);
const L = ({ children }: { children: React.ReactNode }) => (
  <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>{children}</Box>
);
const Li = ({ children }: { children: React.ReactNode }) => (
  <Box component="li" sx={{ mb: 0.5, fontSize: '0.95rem', lineHeight: 1.6 }}>{children}</Box>
);

const Section_privacy: GuideSection = {
  id: 'privacy',
  label: 'Privacy Policy',
  group: 'help',
  icon: <PrivacyTipIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>Privacy Policy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Effective: 2026-05-30 · Version 2026.05 · Governed by the DPDP Act 2023
      </Typography>

      <GuideCallout kind="warning" title="Draft pending counsel review">
        Prepared from general knowledge of India's Digital Personal Data Protection
        Act, 2023 and the IT Rules. Not yet attorney-vetted. The operator will
        replace this text with a counsel-reviewed version before unrestricted
        public launch.
      </GuideCallout>

      <H id="who">1. Who is the Data Fiduciary</H>
      <P>
        The Operator of the earngush Platform (<strong>https://earngush.com</strong>) is
        the "Data Fiduciary" under the Digital Personal Data Protection Act, 2023
        ("DPDP Act") in respect of personal data you provide to the Platform. The
        Operator's grievance officer can be reached at the address published on
        the Platform's home page.
      </P>

      <H id="what">2. What personal data we collect</H>
      <P><strong>Identification &amp; contact:</strong></P>
      <L>
        <Li>Name and email address (required, supplied at registration).</Li>
        <Li>Profile picture URL (optional, supplied by you).</Li>
        <Li>Telegram chat ID and bot token (only if you opt into Telegram notifications, supplied by you).</Li>
        <Li>Mobile number for forgot-password delivery, if you choose to set one up via Telegram.</Li>
      </L>
      <P><strong>Authentication:</strong></P>
      <L>
        <Li>Hashed password (bcrypt, cost-12; the plaintext is never stored or transmitted after the moment of submission).</Li>
        <Li>Time-of-last-login (for session management and security review).</Li>
        <Li>JSON Web Tokens issued at login; refresh tokens stored server-side in Redis with a 7-day TTL.</Li>
        <Li>If you enable 2FA, a TOTP secret (encrypted at rest) and one-time recovery codes (hashed).</Li>
      </L>
      <P><strong>Broker integration:</strong></P>
      <L>
        <Li>Broker API key and API secret for each broker you connect (Zerodha, Delta Exchange). API SECRETS are encrypted at rest with AES-256-GCM using a key that is held outside the application database. API KEYS are also encrypted as of Round 80.</Li>
        <Li>Webhook secret used to authenticate TradingView traffic to your account. Stored per-user.</Li>
      </L>
      <P><strong>Trading data:</strong></P>
      <L>
        <Li>Alerts received (from TradingView, internal scanners, manual entry), orders placed, trades opened / closed, P&amp;L history, strategy configurations, watchlists, chart preferences.</Li>
        <Li>Audit events (login, password change, settings change, role change, legal acceptance, etc.) for security and compliance review.</Li>
      </L>
      <P><strong>Technical &amp; usage data:</strong></P>
      <L>
        <Li>IP address of the requesting client (logged on every request for rate-limiting and security review).</Li>
        <Li>HTTP user agent.</Li>
        <Li>Server-side application logs (the Operator endeavours not to log full personal data; query parameters that contain secrets are redacted).</Li>
        <Li>Browser local storage values for UI preferences (chart settings, sort orders) — these never leave your device.</Li>
        <Li>If Sentry error tracking is enabled by environment configuration, stack traces and error context are forwarded to Sentry; PII is scrubbed prior to dispatch where reasonably practicable.</Li>
      </L>
      <P>
        The Platform does <strong>not</strong> collect or store your government-issued IDs
        (PAN, Aadhaar, voter ID, passport). Such identifiers are held only by your
        broker, not by us.
      </P>

      <H id="purpose">3. Why we collect it (purposes &amp; legal basis)</H>
      <P>
        Under DPDP Act §6, personal data is processed for the following specified
        purposes, on the legal basis of your consent given at the legal-acceptance
        gate and at each opt-in point:
      </P>
      <L>
        <Li>To provide the core service: authenticate you, route TradingView webhooks, place orders against your brokers as instructed, present positions and P&amp;L to you.</Li>
        <Li>To deliver notifications you have configured (Telegram alerts, in-app toasts).</Li>
        <Li>To maintain an audit trail for security, fraud detection, and your own dispute-resolution needs.</Li>
        <Li>To enforce risk controls (per-user daily loss cap, kill switch, expiry filters).</Li>
        <Li>To debug and improve the Platform (server logs, error tracking).</Li>
        <Li>To comply with a lawful request from a court, regulator, or law-enforcement authority of competent jurisdiction in India.</Li>
      </L>

      <H id="share">4. Who we share it with</H>
      <P>
        The Operator does <strong>not</strong> sell personal data to anyone, ever.
        Limited disclosure occurs to the following categories of Data Processors,
        each under contract or click-through terms binding them to confidentiality
        and security:
      </P>
      <L>
        <Li><strong>Brokers and exchanges</strong> — Zerodha, Delta Exchange, NSE/BSE/MCX intermediaries — when the Platform routes orders or fetches positions, on your instructions.</Li>
        <Li><strong>Hosting provider</strong> — Contabo GmbH (Germany) — for the Virtual Private Server that runs the Platform.</Li>
        <Li><strong>Telegram</strong> — for delivery of notifications you have opted into.</Li>
        <Li><strong>Sentry</strong> (functionalsoftware Inc.) — for error tracking, only if enabled.</Li>
        <Li><strong>Government and regulators</strong> — on a binding lawful request only; the Operator will challenge over-broad requests where lawful to do so.</Li>
      </L>
      <P>
        The Platform's hosting is currently in Germany (Contabo). By using the
        Platform you consent to your personal data being processed and stored
        outside India in jurisdictions providing reasonably equivalent data
        protection (the EU's GDPR applies to Contabo).
      </P>

      <H id="retention">5. How long we keep it</H>
      <L>
        <Li>Account profile, trading history, and audit events: retained for as long as your account is active and for up to <strong>seven (7) years</strong> after account closure, to satisfy India's tax-record retention norms and exchange-record retention norms.</Li>
        <Li>Soft-deleted accounts enter a thirty (30) day grace window before the system purges related children; recovery during this window requires writing to the Operator.</Li>
        <Li>Server logs are rotated and retained for no more than ninety (90) days.</Li>
        <Li>Sentry breadcrumbs and error events are subject to Sentry's own retention (typically 90 days).</Li>
      </L>

      <H id="security">6. How we protect it</H>
      <L>
        <Li>HTTPS / TLS 1.2+ for every request via Let's Encrypt certificates.</Li>
        <Li>Passwords stored as bcrypt cost-12 hashes; legacy SHA-256 hashes are rehashed silently on the next successful login.</Li>
        <Li>Broker API secrets and API keys encrypted at rest using AES-256-GCM; encryption key held outside the database and validated at boot.</Li>
        <Li>Per-user webhook secrets so a leak does not allow cross-user impersonation.</Li>
        <Li>Helmet HTTP security headers (HSTS, referrer policy, frame-ancestors).</Li>
        <Li>Rate limiting on authentication endpoints (keyed by IP + email).</Li>
        <Li>Append-only audit log of security-sensitive actions.</Li>
        <Li>Backend Sentry capture of unhandled exceptions where enabled.</Li>
        <Li>Daily Postgres backups retained on host plus an off-host copy.</Li>
      </L>
      <P>
        No system is perfectly secure. If the Operator becomes aware of a personal-data
        breach affecting you, you will be notified consistent with §8 of the DPDP Act
        and the Operator's reasonable timeline for facts to stabilise.
      </P>

      <H id="rights">7. Your rights as a Data Principal</H>
      <P>Under the DPDP Act you have the right to:</P>
      <L>
        <Li>Obtain a summary of the personal data the Operator processes about you and the processing activities undertaken.</Li>
        <Li>Correct inaccurate or out-of-date personal data, and complete incomplete data.</Li>
        <Li>Erase personal data that is no longer necessary for the purpose for which it was collected. Note that some data must be retained for the seven-year statutory window.</Li>
        <Li>Nominate another individual who may exercise your rights in the event of your death or incapacity.</Li>
        <Li>Withdraw consent for processing at any time. Withdrawal will be effective prospectively and will require closure of the account because the Platform cannot function without the data listed in §2.</Li>
        <Li>Lodge a grievance with the Operator's grievance officer; if unresolved within fifteen (15) days, escalate to the Data Protection Board of India.</Li>
      </L>
      <P>
        Most of these can be exercised directly from Settings (data export, account
        deletion). Where the UI does not yet cover a request, write to the grievance
        officer.
      </P>

      <H id="children">8. Children</H>
      <P>
        The Platform is not directed to anyone under 18 and the Operator does not
        knowingly collect personal data from minors. If you believe a minor has
        registered, notify the grievance officer; the account will be terminated
        and data purged.
      </P>

      <H id="cookies">9. Cookies &amp; local storage</H>
      <P>
        The Platform uses strictly-necessary first-party local storage to hold
        UI preferences (chart settings, sort order, theme) and an authentication
        JWT issued on sign-in. No third-party advertising or analytics cookies
        are set. EU/EEA visitors are shown a cookie-consent banner at first
        visit.
      </P>

      <H id="changes">10. Changes to this Policy</H>
      <P>
        Material changes will be signalled by a bump of the legal version constant,
        triggering the in-app re-acceptance gate for every user on next sign-in.
        Trivial changes (typo, formatting) do not trigger a re-prompt; the latest
        text is always the canonical version.
      </P>

      <H id="grievance">11. Grievance officer</H>
      <P>
        Contact the Grievance Officer at the address published on the Platform's
        home page or in your account settings. The Operator will acknowledge your
        request within forty-eight (48) hours and respond substantively within
        fifteen (15) days.
      </P>
    </>
  ),
};

export default Section_privacy;
